import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Upload, Download, AlertCircle, Circle, RotateCcw } from 'lucide-react';

type Shape = 'circle' | 'rounded' | 'square' | 'ellipse';
type BorderStyle = 'solid' | 'dashed' | 'double';
type BgOption = 'transparent' | 'white' | 'custom';
type OutputFormat = 'png' | 'jpg';

const SHAPES: { key: Shape; label: string; icon: string }[] = [
  { key: 'circle', label: 'Circle', icon: '\u25CB' },
  { key: 'rounded', label: 'Rounded', icon: '\u25A2' },
  { key: 'square', label: 'Square', icon: '\u25A1' },
  { key: 'ellipse', label: 'Ellipse', icon: '\u2B2D' },
];

const RoundImage: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [originalPreview, setOriginalPreview] = useState<string | null>(null);
  const [shape, setShape] = useState<Shape>('circle');
  const [cornerRadius, setCornerRadius] = useState(25);
  const [borderWidth, setBorderWidth] = useState(0);
  const [borderColor, setBorderColor] = useState('#ffffff');
  const [borderStyle, setBorderStyle] = useState<BorderStyle>('solid');
  const [showShadow, setShowShadow] = useState(false);
  const [shadowBlur, setShadowBlur] = useState(10);
  const [shadowOffset, setShadowOffset] = useState(4);
  const [shadowColor, setShadowColor] = useState('#00000066');
  const [bgOption, setBgOption] = useState<BgOption>('transparent');
  const [customBg, setCustomBg] = useState('#ffffff');
  const [padding, setPadding] = useState(0);
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('png');
  const [error, setError] = useState<string | null>(null);
  const [imgElement, setImgElement] = useState<HTMLImageElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);

  const handleFileSelect = useCallback((selectedFiles: FileList | null) => {
    if (!selectedFiles || selectedFiles.length === 0) return;
    const f = selectedFiles[0];
    if (!f.type.startsWith('image/')) {
      setError('Please select an image file.');
      return;
    }
    setFile(f);
    setError(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      const url = e.target?.result as string;
      setOriginalPreview(url);
      const img = new window.Image();
      img.onload = () => setImgElement(img);
      img.onerror = () => setError('Failed to load image.');
      img.src = url;
    };
    reader.readAsDataURL(f);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const drawRound = useCallback((canvas: HTMLCanvasElement, img: HTMLImageElement) => {
    const imgW = img.naturalWidth;
    const imgH = img.naturalHeight;

    // For circle/square, use min dimension. For ellipse/rounded, use original dims.
    let drawW: number, drawH: number;
    if (shape === 'circle' || shape === 'square') {
      const size = Math.min(imgW, imgH);
      drawW = size;
      drawH = size;
    } else {
      drawW = imgW;
      drawH = imgH;
    }

    const shadowExtra = showShadow ? Math.ceil(shadowBlur + Math.abs(shadowOffset)) * 2 : 0;
    const totalW = drawW + borderWidth * 2 + padding * 2 + shadowExtra;
    const totalH = drawH + borderWidth * 2 + padding * 2 + shadowExtra;

    canvas.width = totalW;
    canvas.height = totalH;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, totalW, totalH);

    // Background
    if (bgOption === 'white') {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, totalW, totalH);
    } else if (bgOption === 'custom') {
      ctx.fillStyle = customBg;
      ctx.fillRect(0, 0, totalW, totalH);
    }

    const centerX = totalW / 2;
    const centerY = totalH / 2;
    const halfW = drawW / 2;
    const halfH = drawH / 2;

    // Shadow
    if (showShadow) {
      ctx.save();
      ctx.shadowColor = shadowColor;
      ctx.shadowBlur = shadowBlur;
      ctx.shadowOffsetX = shadowOffset;
      ctx.shadowOffsetY = shadowOffset;

      ctx.beginPath();
      if (shape === 'circle') {
        ctx.arc(centerX, centerY, halfW + borderWidth, 0, Math.PI * 2);
      } else if (shape === 'ellipse') {
        ctx.ellipse(centerX, centerY, halfW + borderWidth, halfH + borderWidth, 0, 0, Math.PI * 2);
      } else if (shape === 'rounded') {
        const r = Math.min(halfW, halfH) * (cornerRadius / 50);
        const x = centerX - halfW - borderWidth;
        const y = centerY - halfH - borderWidth;
        const w = (halfW + borderWidth) * 2;
        const h = (halfH + borderWidth) * 2;
        ctx.roundRect(x, y, w, h, r);
      } else {
        ctx.rect(centerX - halfW - borderWidth, centerY - halfH - borderWidth, (halfW + borderWidth) * 2, (halfH + borderWidth) * 2);
      }
      ctx.fillStyle = borderWidth > 0 ? borderColor : '#ffffff';
      ctx.fill();
      ctx.restore();
    }

    // Border
    if (borderWidth > 0) {
      ctx.beginPath();
      if (shape === 'circle') {
        ctx.arc(centerX, centerY, halfW + borderWidth, 0, Math.PI * 2);
      } else if (shape === 'ellipse') {
        ctx.ellipse(centerX, centerY, halfW + borderWidth, halfH + borderWidth, 0, 0, Math.PI * 2);
      } else if (shape === 'rounded') {
        const r = Math.min(halfW + borderWidth, halfH + borderWidth) * (cornerRadius / 50);
        ctx.roundRect(centerX - halfW - borderWidth, centerY - halfH - borderWidth, (halfW + borderWidth) * 2, (halfH + borderWidth) * 2, r);
      } else {
        ctx.rect(centerX - halfW - borderWidth, centerY - halfH - borderWidth, (halfW + borderWidth) * 2, (halfH + borderWidth) * 2);
      }

      ctx.fillStyle = borderColor;
      if (borderStyle === 'dashed') {
        ctx.setLineDash([8, 4]);
        ctx.lineWidth = borderWidth * 2;
        ctx.strokeStyle = borderColor;
        ctx.stroke();
        ctx.setLineDash([]);
      } else if (borderStyle === 'double') {
        ctx.fill();
        // Inner gap
        ctx.globalCompositeOperation = 'destination-out';
        ctx.beginPath();
        const gap = borderWidth * 0.35;
        if (shape === 'circle') {
          ctx.arc(centerX, centerY, halfW + gap, 0, Math.PI * 2);
        } else if (shape === 'ellipse') {
          ctx.ellipse(centerX, centerY, halfW + gap, halfH + gap, 0, 0, Math.PI * 2);
        } else if (shape === 'rounded') {
          const r = Math.min(halfW + gap, halfH + gap) * (cornerRadius / 50);
          ctx.roundRect(centerX - halfW - gap, centerY - halfH - gap, (halfW + gap) * 2, (halfH + gap) * 2, r);
        } else {
          ctx.rect(centerX - halfW - gap, centerY - halfH - gap, (halfW + gap) * 2, (halfH + gap) * 2);
        }
        ctx.fill();
        ctx.globalCompositeOperation = 'source-over';
        // Inner border
        ctx.beginPath();
        const inner = borderWidth * 0.15;
        if (shape === 'circle') {
          ctx.arc(centerX, centerY, halfW + inner, 0, Math.PI * 2);
        } else if (shape === 'ellipse') {
          ctx.ellipse(centerX, centerY, halfW + inner, halfH + inner, 0, 0, Math.PI * 2);
        } else if (shape === 'rounded') {
          const r = Math.min(halfW + inner, halfH + inner) * (cornerRadius / 50);
          ctx.roundRect(centerX - halfW - inner, centerY - halfH - inner, (halfW + inner) * 2, (halfH + inner) * 2, r);
        } else {
          ctx.rect(centerX - halfW - inner, centerY - halfH - inner, (halfW + inner) * 2, (halfH + inner) * 2);
        }
        ctx.fillStyle = borderColor;
        ctx.fill();
      } else {
        ctx.fill();
      }
    }

    // Clip to shape and draw image
    ctx.save();
    ctx.beginPath();
    if (shape === 'circle') {
      ctx.arc(centerX, centerY, halfW, 0, Math.PI * 2);
    } else if (shape === 'ellipse') {
      ctx.ellipse(centerX, centerY, halfW, halfH, 0, 0, Math.PI * 2);
    } else if (shape === 'rounded') {
      const r = Math.min(halfW, halfH) * (cornerRadius / 50);
      ctx.roundRect(centerX - halfW, centerY - halfH, halfW * 2, halfH * 2, r);
    } else {
      ctx.rect(centerX - halfW, centerY - halfH, halfW * 2, halfH * 2);
    }
    ctx.closePath();
    ctx.clip();

    // Center crop the image
    const sx = (imgW - drawW) / 2;
    const sy = (imgH - drawH) / 2;
    ctx.drawImage(img, sx, sy, drawW, drawH, centerX - halfW, centerY - halfH, drawW, drawH);
    ctx.restore();
  }, [shape, cornerRadius, borderWidth, borderColor, borderStyle, showShadow, shadowBlur, shadowOffset, shadowColor, bgOption, customBg, padding]);

  // Live preview
  useEffect(() => {
    if (!imgElement || !previewCanvasRef.current) return;
    const timer = setTimeout(() => {
      drawRound(previewCanvasRef.current!, imgElement);
    }, 50);
    return () => clearTimeout(timer);
  }, [imgElement, drawRound]);

  const downloadResult = () => {
    if (!imgElement || !file) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    drawRound(canvas, imgElement);
    const mimeType = outputFormat === 'jpg' ? 'image/jpeg' : 'image/png';
    const quality = outputFormat === 'jpg' ? 0.92 : undefined;
    canvas.toBlob((blob) => {
      if (!blob) return;
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      const name = file.name.replace(/\.[^.]+$/, '');
      link.download = `${name}_${shape}.${outputFormat}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    }, mimeType, quality);
  };

  const reset = () => {
    setFile(null);
    setOriginalPreview(null);
    setImgElement(null);
    setShape('circle');
    setCornerRadius(25);
    setBorderWidth(0);
    setBorderColor('#ffffff');
    setBorderStyle('solid');
    setShowShadow(false);
    setPadding(0);
    setBgOption('transparent');
    setOutputFormat('png');
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const checkerboardStyle: React.CSSProperties = {
    backgroundImage: `linear-gradient(45deg, #ccc 25%, transparent 25%),
      linear-gradient(-45deg, #ccc 25%, transparent 25%),
      linear-gradient(45deg, transparent 75%, #ccc 75%),
      linear-gradient(-45deg, transparent 75%, #ccc 75%)`,
    backgroundSize: '20px 20px',
    backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px',
  };

  return (
    <div className="space-y-6">
      <canvas ref={canvasRef} className="hidden" />

      {/* Header */}
      <div className="text-center">
        <div className="flex justify-center mb-4">
          <div className="p-3 bg-fuchsia-100 rounded-full">
            <Circle className="w-8 h-8 text-fuchsia-600" />
          </div>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Round / Shape Crop Image</h2>
        <p className="text-gray-600">
          Crop images into circles, rounded squares, ellipses with borders and shadows.
        </p>
      </div>

      {/* Upload */}
      {!file && (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-fuchsia-400 transition-colors cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Drop an image here or click to select
          </h3>
          <p className="text-gray-600">Supports PNG, JPG, WebP and more</p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={(e) => handleFileSelect(e.target.files)}
            className="hidden"
          />
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
          <AlertCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {file && imgElement && (
        <div className="space-y-4">
          {/* Shape Selector */}
          <div className="bg-gray-50 rounded-xl p-4">
            <label className="text-sm font-medium text-gray-700 mb-3 block">Shape</label>
            <div className="grid grid-cols-4 gap-2">
              {SHAPES.map((s) => (
                <button
                  key={s.key}
                  onClick={() => setShape(s.key)}
                  className={`flex flex-col items-center gap-1 px-3 py-3 rounded-lg text-sm font-medium transition-colors ${
                    shape === s.key
                      ? 'bg-fuchsia-600 text-white shadow-sm'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  <span className="text-2xl leading-none">{s.icon}</span>
                  <span className="text-xs">{s.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Corner Radius (Rounded shape only) */}
          {shape === 'rounded' && (
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-medium text-gray-700">Corner Radius</label>
                <span className="text-sm font-bold text-fuchsia-600">{cornerRadius}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="50"
                value={cornerRadius}
                onChange={(e) => setCornerRadius(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-fuchsia-600"
              />
            </div>
          )}

          {/* Border Options */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-4">
            <label className="text-sm font-medium text-gray-700 block">Border</label>
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs text-gray-500">Width</span>
                  <span className="text-xs font-bold text-gray-700">{borderWidth}px</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="20"
                  value={borderWidth}
                  onChange={(e) => setBorderWidth(parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-fuchsia-600"
                />
              </div>
              <div>
                <span className="text-xs text-gray-500 block mb-1">Color</span>
                <input
                  type="color"
                  value={borderColor}
                  onChange={(e) => setBorderColor(e.target.value)}
                  className="w-full h-9 rounded-lg cursor-pointer border border-gray-300"
                />
              </div>
              <div>
                <span className="text-xs text-gray-500 block mb-1">Style</span>
                <div className="flex gap-1">
                  {(['solid', 'dashed', 'double'] as BorderStyle[]).map((s) => (
                    <button
                      key={s}
                      onClick={() => setBorderStyle(s)}
                      className={`flex-1 px-2 py-1.5 rounded text-xs font-medium capitalize transition-colors ${
                        borderStyle === s
                          ? 'bg-fuchsia-600 text-white'
                          : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Shadow Options */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">Shadow</label>
              <button
                onClick={() => setShowShadow(!showShadow)}
                className={`relative w-10 h-5 rounded-full transition-colors ${
                  showShadow ? 'bg-fuchsia-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                    showShadow ? 'translate-x-5' : ''
                  }`}
                />
              </button>
            </div>
            {showShadow && (
              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-gray-500">Blur</span>
                    <span className="font-bold text-gray-700">{shadowBlur}px</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="40"
                    value={shadowBlur}
                    onChange={(e) => setShadowBlur(parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-fuchsia-600"
                  />
                </div>
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-gray-500">Offset</span>
                    <span className="font-bold text-gray-700">{shadowOffset}px</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="20"
                    value={shadowOffset}
                    onChange={(e) => setShadowOffset(parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-fuchsia-600"
                  />
                </div>
                <div>
                  <span className="text-xs text-gray-500 block mb-1">Color</span>
                  <input
                    type="color"
                    value={shadowColor.slice(0, 7)}
                    onChange={(e) => setShadowColor(e.target.value + '66')}
                    className="w-full h-9 rounded-lg cursor-pointer border border-gray-300"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Background & Padding */}
          <div className="bg-gray-50 rounded-xl p-4 grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Background</label>
              <div className="flex gap-1">
                {(['transparent', 'white', 'custom'] as BgOption[]).map((opt) => (
                  <button
                    key={opt}
                    onClick={() => setBgOption(opt)}
                    className={`flex-1 px-2 py-1.5 rounded text-xs font-medium capitalize transition-colors ${
                      bgOption === opt
                        ? 'bg-fuchsia-600 text-white'
                        : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
              {bgOption === 'custom' && (
                <input
                  type="color"
                  value={customBg}
                  onChange={(e) => setCustomBg(e.target.value)}
                  className="w-full h-8 rounded-lg cursor-pointer border border-gray-300 mt-2"
                />
              )}
            </div>
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-medium text-gray-700">Padding</label>
                <span className="text-xs font-bold text-gray-700">{padding}px</span>
              </div>
              <input
                type="range"
                min="0"
                max="50"
                value={padding}
                onChange={(e) => setPadding(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-fuchsia-600"
              />
            </div>
          </div>

          {/* Preview */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-700">Preview</h3>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <span className="text-xs text-gray-500 mb-1 block">Original</span>
                <div className="bg-gray-100 rounded-xl overflow-hidden flex items-center justify-center min-h-[200px]">
                  {originalPreview && (
                    <img src={originalPreview} alt="Original" className="max-w-full max-h-[400px] object-contain" />
                  )}
                </div>
              </div>
              <div>
                <span className="text-xs text-gray-500 mb-1 block">Result</span>
                <div
                  className="rounded-xl overflow-hidden flex items-center justify-center min-h-[200px]"
                  style={bgOption === 'transparent' ? checkerboardStyle : undefined}
                >
                  <canvas ref={previewCanvasRef} className="max-w-full max-h-[400px] object-contain" />
                </div>
              </div>
            </div>
          </div>

          {/* Download Options */}
          <div className="bg-gray-50 rounded-xl p-4">
            <label className="text-sm font-medium text-gray-700 mb-2 block">Output Format</label>
            <div className="flex gap-2">
              <button
                onClick={() => setOutputFormat('png')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  outputFormat === 'png'
                    ? 'bg-fuchsia-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                PNG (transparency)
              </button>
              <button
                onClick={() => setOutputFormat('jpg')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  outputFormat === 'jpg'
                    ? 'bg-fuchsia-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                JPG (with background)
              </button>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={downloadResult}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-fuchsia-600 text-white font-medium rounded-xl hover:bg-fuchsia-700 transition-colors"
            >
              <Download className="w-5 h-5" />
              Download {shape === 'circle' ? 'Circle' : shape === 'ellipse' ? 'Ellipse' : shape === 'rounded' ? 'Rounded' : 'Square'} Image
            </button>
            <button
              onClick={reset}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-300 transition-colors"
            >
              <RotateCcw className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoundImage;
